import { httpClient } from "@/lib/http";
import { ADMIN_MANAGEMENT_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type {
    Admin,
    AdminInvitation,
    AdminsListResponse,
    AdminsQueryParams,
    InviteAdminRequest,
    InviteAdminResponse,
    DeleteAdminRequest,
    DeleteAdminResponse,
    PermissionCatalogResponse,
    UpdateAdminRequest,
    UpdateAdminPermissionsRequest,
    UpdateAdminPermissionsResponse,
    UpdateAdminResponse,
    UpdateAdminStatusRequest,
    UpdateAdminStatusResponse,
    ResetAdminPasswordRequest,
    ResetPasswordResponse,
} from "../types";

// ============================================================================
// Helper Functions
// ============================================================================

const buildQueryString = (params: AdminsQueryParams): string => {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append("page", params.page.toString());
    if (params.size !== undefined) queryParams.append("size", params.size.toString());

    return queryParams.toString();
};

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get paginated list of all admins
 * Requires SUPER_ADMIN role
 */
export const getAdmins = async (params: AdminsQueryParams = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString
        ? `${ADMIN_MANAGEMENT_ENDPOINTS.LIST}?${queryString}`
        : ADMIN_MANAGEMENT_ENDPOINTS.LIST;
    const { data } = await httpClient.get<ApiResponse<AdminsListResponse>>(url);
    return data;
};

/**
 * Get details of a specific admin
 * Requires SUPER_ADMIN role
 */
export const getAdminDetails = async (adminId: string) => {
    const { data } = await httpClient.get<ApiResponse<Admin>>(
        ADMIN_MANAGEMENT_ENDPOINTS.DETAILS(adminId)
    );
    return data;
};

export const getPermissionsCatalog = async () => {
    const { data } = await httpClient.get<ApiResponse<PermissionCatalogResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.PERMISSIONS_CATALOG
    );
    return data;
};

export const inviteAdmin = async (payload: InviteAdminRequest) => {
    const { data } = await httpClient.post<ApiResponse<InviteAdminResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.INVITATIONS,
        payload
    );
    return data;
};

export const resendAdminInvitation = async (invitationId: string) => {
    const { data } = await httpClient.post<ApiResponse<AdminInvitation>>(
        ADMIN_MANAGEMENT_ENDPOINTS.RESEND_INVITATION(invitationId)
    );
    return data;
};

/**
 * Update an admin's profile information
 * Requires SUPER_ADMIN role
 */
export const updateAdmin = async (adminId: string, payload: UpdateAdminRequest) => {
    const { data } = await httpClient.put<ApiResponse<UpdateAdminResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.UPDATE(adminId),
        payload
    );
    return data;
};

export const updateAdminPermissions = async (
    adminId: string,
    payload: UpdateAdminPermissionsRequest
) => {
    const { data } = await httpClient.put<ApiResponse<UpdateAdminPermissionsResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.PERMISSIONS(adminId),
        payload
    );
    return data;
};

/**
 * Update an admin's account status
 * Requires SUPER_ADMIN role
 */
export const updateAdminStatus = async (
    adminId: string,
    payload: UpdateAdminStatusRequest
) => {
    const { data } = await httpClient.put<ApiResponse<UpdateAdminStatusResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.STATUS(adminId),
        payload
    );
    return data;
};

/**
 * Reset an admin's password
 * Requires SUPER_ADMIN role
 * Note: Cannot be used to reset your own password
 */
export const resetAdminPassword = async (adminId: string, payload: ResetAdminPasswordRequest) => {
    const { data } = await httpClient.post<ApiResponse<ResetPasswordResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.RESET_PASSWORD(adminId),
        payload
    );
    return data;
};

/**
 * Delete an admin account
 * Requires SUPER_ADMIN role
 */
export const deleteAdmin = async (adminId: string, payload?: DeleteAdminRequest) => {
    const { data } = await httpClient.delete<ApiResponse<DeleteAdminResponse>>(
        ADMIN_MANAGEMENT_ENDPOINTS.DELETE(adminId),
        payload ? { data: payload } : undefined
    );
    return data;
};
